/**
 * Pure layout helpers for the knockout bracket.
 *
 * Extracted from `Bracket/index.svelte` to keep the component focused on
 * orchestration/markup and make these calculations easy to unit test.
 */

import { curveStep, line } from 'd3-shape';

import type { ScaleBand } from 'd3-scale';

const elbow = line().curve(curveStep);

/**
 * Build the SVG `d` attribute for an "elbow" connector between two points.
 */
export const drawElbow = (
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number
): string | null =>
  elbow([
    [sourceX, sourceY],
    [targetX, targetY],
  ]);

/** A single match enriched with the geometry needed to render it. */
export type MatchWithLayout = {
  x: number;
  y: number;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  [key: string]: unknown;
};

/** A round of matches plus its translation in the bracket grid. */
export type RoundWithLayout = {
  round: string;
  xPos: number;
  yPos: number;
  matches: MatchWithLayout[];
  [key: string]: unknown;
};

type Round = {
  round: string;
  matches: Array<Record<string, unknown>>;
};

/**
 * Compute x/y layout for every match in every round.
 *
 * Each match gets `x`, `y` (top-left in the round) plus the source/target
 * coordinates needed by the connector layer.
 */
export const computeMatchesLayout = (
  rounds: Round[],
  xScale: ScaleBand<number>,
  yScale: ScaleBand<number>,
  widthRect: number,
  heightRect: number
): RoundWithLayout[] => {
  const numRounds = rounds.length;

  return rounds.map((round, roundNum) => {
    if (roundNum === numRounds) {
      return {
        ...round,
        xPos: 0,
        yPos: 0,
        matches: round.matches as MatchWithLayout[],
      };
    }

    const xPos = 0;
    const yPos = roundNum === 0 ? 0 : (heightRect + yScale.bandwidth()) / 2;

    const matchesWithLayout: MatchWithLayout[] = round.matches.map(
      (match, i) => {
        const x = xScale(roundNum) ?? 0;
        const y =
          (roundNum === 0 ?
            yScale(i)
          : yScale(i * 2 ** roundNum + (2 ** (roundNum - 1) - 1))) ?? 0;
        const yScaleValue =
          yScale(
            Math.floor(i / 2) * 2 ** (roundNum + 1) + (2 ** roundNum - 1)
          ) ?? 0;
        const targetY =
          roundNum === 0 ?
            heightRect / 2 + (heightRect + yScale.bandwidth()) / 2 + yScaleValue
          : heightRect / 2 + yScaleValue;

        return {
          ...match,
          x,
          y,
          sourceX: x + widthRect,
          sourceY: y + heightRect / 2,
          targetX: x + widthRect + xScale.bandwidth() / 2 - 30,
          targetY,
        };
      }
    );

    return {
      ...round,
      xPos,
      yPos,
      matches: matchesWithLayout,
    };
  });
};
